import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs } from '@mui/material';
import District from '../../model/District';
import {
	autoAssignStage3JuryPairs,
	createStage3JuryFoldersOnly,
	createStage3SecretaryFoldersOnly,
	createStage3JuryPair,
	deleteStage3JuryPair,
	getStage3DriveCopyCandidates,
	getStage3DriveSetup,
	getStage3JuryPairs,
	markStage3AutoScanOkDistrict,
	markStage3AutoScanOkStudent,
	mergeStage3DrivePages,
	replaceStage3DrivePages,
	runStage3DriveCopy,
	runStage3DriveHealthCheck,
	saveStage3DriveSetup,
	searchStage3DriveFolderFiles,
	shareStage3JuryFoldersOnly,
	shareStage3SecretaryFoldersOnly,
	rotateStage3RawDriveFile,
	updateStage3JuryPair,
	verifyStage3JuryFoldersOnly,
	verifyStage3SecretaryFoldersOnly,
	verifyStage3DriveJuryFolders,
	verifyStage3DriveSecretaries,
} from '../../api/content-api';
import { Stage3JuryPairItem } from './Stage3JuryPairsList';
import { Stage3JuryPairDraft } from './Stage3JuryPairForm';
import { CopyCandidate, FoundScanRow, SecretarySettingRow } from './Stage3JuryPairs.types';
import Stage3JuryPairsManagementTab from './Stage3JuryPairsManagementTab';
import Stage3AssignmentTab from './Stage3AssignmentTab';
import Stage3DriveSetupTab from './Stage3DriveSetupTab';
import Stage3DriveVerificationTab from './Stage3DriveVerificationTab';
import Stage3DriveCopyTab from './Stage3DriveCopyTab';
import Stage3DrivePreviewDialog from './Stage3DrivePreviewDialog';
import Stage3DriveSecretariesTab from './Stage3DriveSecretariesTab';
import Stage3RawRotateTab, { Stage3RawRotateFileRow } from './Stage3RawRotateTab';

const EMPTY_DRAFT: Stage3JuryPairDraft = {
	id: null,
	name: '',
	juror1_name: '',
	juror1_email: '',
	juror2_name: '',
	juror2_email: '',
	juror1_district_id: '',
	juror2_district_id: '',
	drive_folder_url: '',
	notes: '',
};

type Props = {
	open: boolean;
	onClose: () => void;
	token: string | null;
	districts: District[];
	onMessage: (msg: { msg: string; severity?: 'success' | 'error' | 'warning' | 'info' }) => void;
	isBusyExternal?: boolean;
};

const Stage3JuryPairsDialog = ({ open, onClose, token, districts, onMessage, isBusyExternal = false }: Props) => {
	const [activeTab, setActiveTab] = useState<
		'secretaries' | 'pairs' | 'assign' | 'copy' | 'verify' | 'setup' | 'tools'
	>('secretaries');
	const [pairs, setPairs] = useState<Stage3JuryPairItem[]>([]);
	const [draft, setDraft] = useState<Stage3JuryPairDraft>(EMPTY_DRAFT);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [driveAccessToken, setDriveAccessToken] = useState('');
	const [driveRefreshToken, setDriveRefreshToken] = useState('');
	const [driveClientId, setDriveClientId] = useState('');
	const [driveClientSecret, setDriveClientSecret] = useState('');
	const [secretariesParentFolderUrl, setSecretariesParentFolderUrl] = useState('');
	const [juryParentFolderUrl, setJuryParentFolderUrl] = useState('');
	const [shareMessage, setShareMessage] = useState('');
	const [testRecipientEmail, setTestRecipientEmail] = useState('');
	const [sendNotificationEmail, setSendNotificationEmail] = useState(true);
	const [shareToSystemEmail, setShareToSystemEmail] = useState(true);
	const [shareToSecondaryEmail, setShareToSecondaryEmail] = useState(false);
	const [overwriteExisting, setOverwriteExisting] = useState(false);
	const [secretariesActionReport, setSecretariesActionReport] = useState<any | null>(null);
	const [juryActionReport, setJuryActionReport] = useState<any | null>(null);
	const [secretaryVerifyStatusByDistrict, setSecretaryVerifyStatusByDistrict] = useState<Record<number, boolean>>({});
	const [secretaryVerifyErrorByDistrict, setSecretaryVerifyErrorByDistrict] = useState<Record<number, string>>({});
	const [juryVerifyStatusByPairId, setJuryVerifyStatusByPairId] = useState<Record<number, boolean>>({});
	const [juryVerifyErrorByPairId, setJuryVerifyErrorByPairId] = useState<Record<number, string>>({});
	const [secretarySettings, setSecretarySettings] = useState<SecretarySettingRow[]>([]);
	const [driveSetupInfo, setDriveSetupInfo] = useState<{
		has_access_token?: boolean;
		has_refresh_token?: boolean;
		has_client_id?: boolean;
		has_client_secret?: boolean;
		access_token_expires_at?: number;
	} | null>(null);
	const [healthReport, setHealthReport] = useState<any | null>(null);
	const [secretaryVerifyReport, setSecretaryVerifyReport] = useState<any | null>(null);
	const [juryVerifyReport, setJuryVerifyReport] = useState<any | null>(null);
	const [copyCandidates, setCopyCandidates] = useState<CopyCandidate[]>([]);
	const [selectedCopyKeys, setSelectedCopyKeys] = useState<Record<string, boolean>>({});
	const [copyRunReport, setCopyRunReport] = useState<any | null>(null);
	const [copySortMode, setCopySortMode] = useState<'districts' | 'pairs' | 'statuses'>('districts');
	const [verifySortMode, setVerifySortMode] = useState<'districts' | 'pairs' | 'statuses'>('districts');
	const [verifyFilterStatus, setVerifyFilterStatus] = useState<
		'new_only' | 'AUTO-SKAN' | 'AUTOSKANOK' | 'not_copied' | 'all_scanned'
	>('new_only');
	const [verifyFilterDistrictId, setVerifyFilterDistrictId] = useState<number>(0);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewRowKey, setPreviewRowKey] = useState<string | null>(null);
	const [autoAssignMode, setAutoAssignMode] = useState<
		'unassigned_only' | 'unassigned_received' | 'assigned_without_received'
	>('unassigned_only');
	const [toolsBaseFolderId, setToolsBaseFolderId] = useState('');
	const [toolsSearchQuery, setToolsSearchQuery] = useState('');
	const [toolsTargetFolderId, setToolsTargetFolderId] = useState('');
	const [toolsFiles, setToolsFiles] = useState<Stage3RawRotateFileRow[]>([]);
	const [toolsTotalMatched, setToolsTotalMatched] = useState(0);
	const [toolsFolderPreset, setToolsFolderPreset] = useState('manual');
	const [toolsMergeQueue, setToolsMergeQueue] = useState<
		Array<{ key: string; drive_file_id: string; drive_file_name: string; page: number }>
	>([]);
	const [toolsReplaceBase, setToolsReplaceBase] = useState<{ drive_file_id: string; drive_file_name: string } | null>(
		null,
	);
	const [toolsReplaceMappings, setToolsReplaceMappings] = useState<
		Array<{
			key: string;
			target_page: number;
			source_drive_file_id: string;
			source_drive_file_name: string;
			source_page: number;
		}>
	>([]);

	const busy = isLoading || isSaving || isBusyExternal;

	const districtNameById = useMemo(() => {
		const map = new Map<number, string>();
		districts.forEach((d) => map.set(Number(d.id), String(d.name)));
		return map;
	}, [districts]);

	const extractDriveId = (value: string): string => {
		const text = String(value || '').trim();
		if (text === '') {
			return '';
		}
		const match = text.match(/[-\w]{20,}/);
		return match ? String(match[0]) : '';
	};

	const toolsFolderPresetOptions = useMemo(() => {
		const options: Array<{ value: string; label: string; folderId: string }> = [
			{ value: 'manual', label: 'Ręcznie', folderId: '' },
		];
		const juryRootId = extractDriveId(String(juryParentFolderUrl || ''));
		if (juryRootId !== '') {
			options.push({
				value: 'root_jury',
				label: 'Root komisji',
				folderId: juryRootId,
			});
		}
		const secRootId = extractDriveId(String(secretariesParentFolderUrl || ''));
		if (secRootId !== '') {
			options.push({
				value: 'root_secretaries',
				label: 'Root sekretarzy/okręgów',
				folderId: secRootId,
			});
		}
		secretarySettings.forEach((row) => {
			const folderId = extractDriveId(String(row.drive_folder_url || ''));
			if (folderId !== '') {
				options.push({
					value: `district_${Number(row.district_id)}`,
					label: `Okręg: ${String(row.district_name || row.district_id)}`,
					folderId,
				});
			}
		});
		pairs.forEach((pair) => {
			const folderId = extractDriveId(String(pair.drive_folder_url || ''));
			if (folderId !== '') {
				options.push({
					value: `pair_${Number(pair.id)}`,
					label: `Komisja: ${String(pair.name || pair.id)}`,
					folderId,
				});
			}
		});
		return options;
	}, [juryParentFolderUrl, secretariesParentFolderUrl, secretarySettings, pairs]);

	const loadPairs = () => {
		setIsLoading(true);
		getStage3JuryPairs(token)
			.then((response) => {
				if (response.status === 200) {
					const list = Array.isArray(response.data?.pairs) ? response.data.pairs : [];
					setPairs(
						list.map((p: any) => ({
							id: Number(p.id),
							name: String(p.name || ''),
							juror1_name: String(p.juror1_name || ''),
							juror1_email: String(p.juror1_email || ''),
							juror2_name: String(p.juror2_name || ''),
							juror2_email: String(p.juror2_email || ''),
							juror1_district_id: p.juror1_district_id !== null ? Number(p.juror1_district_id) : null,
							juror2_district_id: p.juror2_district_id !== null ? Number(p.juror2_district_id) : null,
							juror1_district_name: String(
								p.juror1_district_name || districtNameById.get(Number(p.juror1_district_id)) || '',
							),
							juror2_district_name: String(
								p.juror2_district_name || districtNameById.get(Number(p.juror2_district_id)) || '',
							),
							drive_folder_url: String(p.drive_folder_url || ''),
							notes: String(p.notes || ''),
							assigned_count: Number(p.assigned_count || 0),
						})),
					);
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się pobrać komisji.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd pobierania komisji.' });
			})
			.finally(() => setIsLoading(false));
	};

	const loadDriveSetup = () => {
		setIsLoading(true);
		getStage3DriveSetup(token)
			.then((response) => {
				if (response.status !== 200) {
					onMessage({ msg: response.data?.message || 'Nie udało się pobrać konfiguracji Drive.' });
					return;
				}
				const setup = response.data || {};
				setDriveSetupInfo({
					has_access_token: Boolean(setup.has_access_token),
					has_refresh_token: Boolean(setup.has_refresh_token),
					has_client_id: Boolean(setup.has_client_id),
					has_client_secret: Boolean(setup.has_client_secret),
					access_token_expires_at: Number(setup.access_token_expires_at || 0),
				});
				setSecretariesParentFolderUrl(String(setup.secretaries_parent_folder_url || ''));
				setJuryParentFolderUrl(String(setup.jury_parent_folder_url || ''));
				const current = Array.isArray(setup.secretary_settings) ? setup.secretary_settings : [];
				const mapByDistrict = new Map<number, any>();
				current.forEach((row: any) => mapByDistrict.set(Number(row?.district_id), row));
				const merged = districts.map((district) => {
					const districtId = Number(district.id);
					const existing = mapByDistrict.get(districtId) || {};
					return {
						district_id: districtId,
						district_name: String(district.name || ''),
						title: String(existing.title || ''),
						full_name: String(existing.full_name || ''),
						phone: String(existing.phone || ''),
						secondary_email: String(existing.secondary_email || ''),
						drive_folder_url: String(existing.drive_folder_url || ''),
						system_email: String(existing.system_email || ''),
					};
				});
				setSecretarySettings(merged);
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd pobierania konfiguracji Drive.' });
			})
			.finally(() => setIsLoading(false));
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (open) {
			setActiveTab('secretaries');
			setDraft(EMPTY_DRAFT);
			setDriveAccessToken('');
			setHealthReport(null);
			setSecretaryVerifyReport(null);
			setJuryVerifyReport(null);
			setCopyCandidates([]);
			setSelectedCopyKeys({});
			setCopyRunReport(null);
			setCopySortMode('districts');
			setVerifySortMode('districts');
			setVerifyFilterStatus('new_only');
			setVerifyFilterDistrictId(0);
			setPreviewOpen(false);
			setPreviewRowKey(null);
			setDriveRefreshToken('');
			setDriveClientId('');
			setDriveClientSecret('');
			setShareMessage('');
			setTestRecipientEmail('');
			setSendNotificationEmail(true);
			setShareToSystemEmail(true);
			setShareToSecondaryEmail(false);
			setOverwriteExisting(false);
			setSecretariesActionReport(null);
			setJuryActionReport(null);
			setSecretaryVerifyStatusByDistrict({});
			setSecretaryVerifyErrorByDistrict({});
			setJuryVerifyStatusByPairId({});
			setJuryVerifyErrorByPairId({});
			setToolsBaseFolderId('');
			setToolsSearchQuery('');
			setToolsTargetFolderId('');
			setToolsFiles([]);
			setToolsTotalMatched(0);
			setToolsFolderPreset('manual');
			setToolsMergeQueue([]);
			setToolsReplaceBase(null);
			setToolsReplaceMappings([]);
			loadPairs();
			loadDriveSetup();
		}
	}, [open]);

	const handleSavePair = () => {
		const payload = {
			name: draft.name,
			juror1_name: draft.juror1_name,
			juror1_email: draft.juror1_email,
			juror2_name: draft.juror2_name,
			juror2_email: draft.juror2_email,
			juror1_district_id: draft.juror1_district_id === '' ? null : draft.juror1_district_id,
			juror2_district_id: draft.juror2_district_id === '' ? null : draft.juror2_district_id,
			drive_folder_url: draft.drive_folder_url,
			notes: draft.notes,
		};
		setIsSaving(true);
		const request = draft.id
			? updateStage3JuryPair(token, Number(draft.id), payload)
			: createStage3JuryPair(token, payload);
		request
			.then((response) => {
				if (response.status === 200) {
					onMessage({
						msg: draft.id ? 'Zapisano zmiany komisji.' : 'Dodano nową komisję.',
						severity: 'success',
					});
					setDraft(EMPTY_DRAFT);
					loadPairs();
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się zapisać komisji.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd zapisu komisji.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleDeletePair = (pair: Stage3JuryPairItem) => {
		const accepted = window.confirm(`Usunąć komisję "${pair.name}"?`);
		if (!accepted) {
			return;
		}
		setIsSaving(true);
		deleteStage3JuryPair(token, pair.id)
			.then((response) => {
				if (response.status === 200) {
					onMessage({ msg: 'Usunięto komisję.', severity: 'success' });
					loadPairs();
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się usunąć komisji.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd usuwania komisji.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleAutoAssign = () => {
		const modeLabel =
			autoAssignMode === 'unassigned_only'
				? 'tylko nieprzypisane'
				: autoAssignMode === 'unassigned_received'
					? 'nieprzydzielone z otrzymanymi pracami'
					: 'przypisane, ale bez otrzymanych prac';
		const accepted = window.confirm(`Wykonać automatyczny przydział (${modeLabel})?`);
		if (!accepted) {
			return;
		}
		setIsSaving(true);
		autoAssignStage3JuryPairs(token, autoAssignMode)
			.then((response) => {
				if (response.status === 200) {
					const assigned = Number(response.data?.assigned_count || 0);
					const fallbackUsed = Boolean(response.data?.fallback_used);
					const remaining = Number(response.data?.remaining_unassigned_count || 0);
					const balanceRisk = Boolean(response.data?.remaining_balance_risk);
					onMessage({
						msg: `Auto-przydział zakończony. Przypisano: ${assigned}. Fallback: ${fallbackUsed ? 'TAK' : 'NIE'}. Pozostało nieprzydzielonych: ${remaining}. Ryzyko nierównowagi: ${balanceRisk ? 'TAK' : 'NIE'}.`,
						severity: 'success',
					});
					loadPairs();
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się wykonać auto-przydziału.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd auto-przydziału.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleSaveDriveSetup = () => {
		setIsSaving(true);
		saveStage3DriveSetup(token, {
			access_token: driveAccessToken.trim() ? driveAccessToken.trim() : undefined,
			refresh_token: driveRefreshToken.trim() ? driveRefreshToken.trim() : undefined,
			client_id: driveClientId.trim() ? driveClientId.trim() : undefined,
			client_secret: driveClientSecret.trim() ? driveClientSecret.trim() : undefined,
			secretaries_parent_folder_url: secretariesParentFolderUrl.trim() || undefined,
			jury_parent_folder_url: juryParentFolderUrl.trim() || undefined,
			secretary_settings: secretarySettings.map((row) => ({
				district_id: row.district_id,
				title: row.title,
				full_name: row.full_name,
				phone: row.phone,
				secondary_email: row.secondary_email,
				drive_folder_url: row.drive_folder_url,
			})),
		})
			.then((response) => {
				if (response.status === 200) {
					setDriveSetupInfo({
						has_access_token: Boolean(response.data?.has_access_token),
						has_refresh_token: Boolean(response.data?.has_refresh_token),
						has_client_id: Boolean(response.data?.has_client_id),
						has_client_secret: Boolean(response.data?.has_client_secret),
						access_token_expires_at: Number(response.data?.access_token_expires_at || 0),
					});
					setDriveAccessToken('');
					setDriveRefreshToken('');
					setDriveClientId('');
					setDriveClientSecret('');
					onMessage({ msg: 'Zapisano konfigurację Drive.', severity: 'success' });
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się zapisać konfiguracji Drive.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd zapisu konfiguracji Drive.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleSecretaryFieldChange = (
		districtId: number,
		field: 'title' | 'full_name' | 'phone' | 'secondary_email' | 'drive_folder_url',
		value: string,
	) => {
		setSecretarySettings((prev) =>
			prev.map((row) => (row.district_id === districtId ? { ...row, [field]: value } : row)),
		);
	};

	const handleCreateSecretaryFolders = () => {
		setIsSaving(true);
		createStage3SecretaryFoldersOnly(token, {
			parent_folder_url: secretariesParentFolderUrl.trim(),
			overwrite_existing: overwriteExisting,
		})
			.then((response) => {
				if (response.status === 200) {
					setSecretariesActionReport(response.data);
					onMessage({ msg: 'Utworzono foldery sekretarzy.', severity: 'success' });
					loadDriveSetup();
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się utworzyć folderów sekretarzy.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd tworzenia folderów sekretarzy.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleVerifySecretaryFoldersOnly = () => {
		setIsSaving(true);
		verifyStage3SecretaryFoldersOnly(token)
			.then((response) => {
				if (response.status === 200) {
					setSecretariesActionReport(response.data);
					const items = Array.isArray(response.data?.items) ? response.data.items : [];
					const nextStatus: Record<number, boolean> = {};
					const nextErrors: Record<number, string> = {};
					items.forEach((row: any) => {
						const districtId = Number(row?.district_id || 0);
						if (districtId <= 0) {
							return;
						}
						nextStatus[districtId] = Boolean(row?.verified);
						nextErrors[districtId] = String(row?.error || '');
					});
					setSecretaryVerifyStatusByDistrict(nextStatus);
					setSecretaryVerifyErrorByDistrict(nextErrors);
					onMessage({ msg: 'Zweryfikowano foldery sekretarzy.', severity: 'success' });
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się zweryfikować folderów sekretarzy.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd weryfikacji folderów sekretarzy.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleShareSecretaryFoldersOnly = () => {
		const shareEnabled = Boolean(testRecipientEmail.trim()) || shareToSystemEmail || shareToSecondaryEmail;
		if (!shareEnabled) {
			onMessage({ msg: 'Wybierz co najmniej jeden kanał udostępniania dla sekretarzy.', severity: 'warning' });
			return;
		}
		setIsSaving(true);
		shareStage3SecretaryFoldersOnly(token, {
			share_message: shareMessage.trim() || undefined,
			test_recipient_email: testRecipientEmail.trim() || undefined,
			send_notification_email: sendNotificationEmail,
			share_system_email: shareToSystemEmail,
			share_secondary_email: shareToSecondaryEmail,
		})
			.then((response) => {
				if (response.status === 200) {
					setSecretariesActionReport(response.data);
					onMessage({ msg: 'Udostępniono foldery sekretarzy.', severity: 'success' });
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się udostępnić folderów sekretarzy.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd udostępniania folderów sekretarzy.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleCreateJuryFolders = () => {
		setIsSaving(true);
		createStage3JuryFoldersOnly(token, {
			parent_folder_url: juryParentFolderUrl.trim(),
			overwrite_existing: overwriteExisting,
		})
			.then((response) => {
				if (response.status === 200) {
					setJuryActionReport(response.data);
					onMessage({ msg: 'Utworzono foldery komisji.', severity: 'success' });
					loadPairs();
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się utworzyć folderów komisji.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd tworzenia folderów komisji.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleVerifyJuryFoldersOnly = () => {
		setIsSaving(true);
		verifyStage3JuryFoldersOnly(token)
			.then((response) => {
				if (response.status === 200) {
					setJuryActionReport(response.data);
					const items = Array.isArray(response.data?.items) ? response.data.items : [];
					const nextStatus: Record<number, boolean> = {};
					const nextErrors: Record<number, string> = {};
					items.forEach((row: any) => {
						const pairId = Number(row?.jury_pair_id || 0);
						if (pairId <= 0) {
							return;
						}
						nextStatus[pairId] = Boolean(row?.verified);
						nextErrors[pairId] = String(row?.error || '');
					});
					setJuryVerifyStatusByPairId(nextStatus);
					setJuryVerifyErrorByPairId(nextErrors);
					onMessage({ msg: 'Zweryfikowano foldery komisji.', severity: 'success' });
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się zweryfikować folderów komisji.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd weryfikacji folderów komisji.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleShareJuryFoldersOnly = () => {
		setIsSaving(true);
		shareStage3JuryFoldersOnly(token, {
			share_message: shareMessage.trim() || undefined,
			test_recipient_email: testRecipientEmail.trim() || undefined,
			send_notification_email: sendNotificationEmail,
		})
			.then((response) => {
				if (response.status === 200) {
					setJuryActionReport(response.data);
					onMessage({ msg: 'Udostępniono foldery komisji.', severity: 'success' });
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się udostępnić folderów komisji.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd udostępniania folderów komisji.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleDriveHealthCheck = () => {
		setIsSaving(true);
		runStage3DriveHealthCheck(token)
			.then((response) => {
				if (response.status === 200) {
					setHealthReport(response.data);
					onMessage({ msg: 'Wykonano health-check Google Drive.', severity: 'success' });
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się wykonać health-check.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd health-check Google Drive.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleVerifySecretaries = () => {
		setIsSaving(true);
		verifyStage3DriveSecretaries(token)
			.then((response) => {
				if (response.status === 200) {
					setSecretaryVerifyReport(response.data);
					const summary = response.data?.summary || {};
					const expectedTotal = Number(summary.expected_total || 0);
					const notedBeforeTotal = Number(summary.noted_before_total || 0);
					const newlyFoundTotal = Number(summary.newly_found_total || 0);
					const updatedTotal = Number(summary.auto_scan_updated_total || 0);
					onMessage({
						msg: `Zweryfikowano foldery sekretarzy. Prac oczekiwanych: ${expectedTotal}, odnotowanych wcześniej: ${notedBeforeTotal}, nowo znalezionych: ${newlyFoundTotal}, ustawiono AUTO-SKAN: ${updatedTotal}.`,
						severity: 'success',
					});
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się zweryfikować folderów sekretarzy.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd weryfikacji folderów sekretarzy.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleVerifyJuryFolders = () => {
		setIsSaving(true);
		verifyStage3DriveJuryFolders(token)
			.then((response) => {
				if (response.status === 200) {
					setJuryVerifyReport(response.data);
					onMessage({ msg: 'Zweryfikowano foldery komisji.', severity: 'success' });
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się zweryfikować folderów komisji.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd weryfikacji folderów komisji.' });
			})
			.finally(() => setIsSaving(false));
	};

	const candidateKey = (candidate: CopyCandidate): string =>
		`${candidate.work_code}::${candidate.source_file_id}::${candidate.target_folder_id}`;

	const handleLoadCopyCandidates = () => {
		setIsSaving(true);
		getStage3DriveCopyCandidates(token)
			.then((response) => {
				if (response.status === 200) {
					const list: CopyCandidate[] = Array.isArray(response.data?.candidates) ? response.data.candidates : [];
					setCopyCandidates(list);
					const selected: Record<string, boolean> = {};
					list.forEach((item) => {
						if (item.copy_allowed && item.copy_default_selected) {
							selected[candidateKey(item)] = true;
						}
					});
					setSelectedCopyKeys(selected);
					onMessage({ msg: 'Wczytano skany dokumentów do skopiowania dla komisji.', severity: 'success' });
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się pobrać kandydatów kopiowania.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd pobierania kandydatów kopiowania.' });
			})
			.finally(() => setIsSaving(false));
	};

	const selectAllCopyAllowed = () => {
		const selected: Record<string, boolean> = {};
		copyCandidates.forEach((item) => {
			if (item.copy_allowed) {
				selected[candidateKey(item)] = true;
			}
		});
		setSelectedCopyKeys(selected);
	};

	const clearCopySelection = () => {
		setSelectedCopyKeys({});
	};

	const toggleCopySelection = (item: CopyCandidate) => {
		const key = candidateKey(item);
		setSelectedCopyKeys((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const selectedCopyItems = useMemo(
		() => copyCandidates.filter((item) => selectedCopyKeys[candidateKey(item)]),
		[copyCandidates, selectedCopyKeys],
	);

	const sortedCopyCandidates = useMemo(() => {
		const rows = [...copyCandidates];
		rows.sort((a, b) => {
			if (copySortMode === 'districts') {
				const district = a.district_name.localeCompare(b.district_name, 'pl');
				if (district !== 0) {
					return district;
				}
				const status = (a.current_status || '').localeCompare(b.current_status || '', 'pl');
				if (status !== 0) {
					return status;
				}
				return a.work_code.localeCompare(b.work_code, 'pl');
			}
			if (copySortMode === 'pairs') {
				const pair = a.jury_pair_name.localeCompare(b.jury_pair_name, 'pl');
				if (pair !== 0) {
					return pair;
				}
				const district = a.district_name.localeCompare(b.district_name, 'pl');
				if (district !== 0) {
					return district;
				}
				return a.work_code.localeCompare(b.work_code, 'pl');
			}
			const status = (a.current_status || '').localeCompare(b.current_status || '', 'pl');
			if (status !== 0) {
				return status;
			}
			const district = a.district_name.localeCompare(b.district_name, 'pl');
			if (district !== 0) {
				return district;
			}
			return a.work_code.localeCompare(b.work_code, 'pl');
		});
		return rows;
	}, [copyCandidates, copySortMode]);

	const foundScanRows = useMemo<FoundScanRow[]>(() => {
		const districtsData = Array.isArray(secretaryVerifyReport?.districts) ? secretaryVerifyReport.districts : [];
		const rows: FoundScanRow[] = [];
		districtsData.forEach((district: any) => {
			const details = Array.isArray(district?.found_details) ? district.found_details : [];
			details.forEach((detail: any) => {
				rows.push({
					district_id: Number(district?.district_id || 0),
					district_name: String(district?.district_name || ''),
					work_code: String(detail?.work_code || ''),
					student_id: Number(detail?.student_id || 0),
					jury_pair_id: Number(detail?.jury_pair_id || 0),
					jury_pair_name: String(detail?.jury_pair_name || ''),
					existing_status: String(detail?.existing_status || ''),
					was_already_noted: Boolean(detail?.was_already_noted),
					drive_copied: Boolean(detail?.drive_copied),
					drive_copied_at: String(detail?.drive_copied_at || ''),
					source_file_id: String(detail?.source_file_id || ''),
					source_file_name: String(detail?.source_file_name || ''),
					source_relative_path: String(detail?.source_relative_path || ''),
					drive_view_url: String(detail?.drive_view_url || ''),
				});
			});
		});
		const filtered = rows.filter((row) => {
			if (verifyFilterDistrictId > 0 && row.district_id !== verifyFilterDistrictId) {
				return false;
			}
			const normalizedStatus = (row.existing_status || '').trim().toUpperCase();
			if (verifyFilterStatus === 'new_only') {
				return !row.was_already_noted;
			}
			if (verifyFilterStatus === 'AUTO-SKAN') {
				return normalizedStatus === 'AUTO-SKAN';
			}
			if (verifyFilterStatus === 'AUTOSKANOK') {
				return normalizedStatus === 'AUTOSKANOK';
			}
			if (verifyFilterStatus === 'not_copied') {
				return !row.drive_copied;
			}
			return true;
		});
		filtered.sort((a, b) => {
			if (verifySortMode === 'districts') {
				const district = a.district_name.localeCompare(b.district_name, 'pl');
				if (district !== 0) {
					return district;
				}
				return a.work_code.localeCompare(b.work_code, 'pl');
			}
			if (verifySortMode === 'pairs') {
				const pair = (a.jury_pair_name || '').localeCompare(b.jury_pair_name || '', 'pl');
				if (pair !== 0) {
					return pair;
				}
				return a.work_code.localeCompare(b.work_code, 'pl');
			}
			const status = (a.existing_status || '').localeCompare(b.existing_status || '', 'pl');
			if (status !== 0) {
				return status;
			}
			return a.district_name.localeCompare(b.district_name, 'pl');
		});
		return filtered;
	}, [secretaryVerifyReport, verifySortMode, verifyFilterDistrictId, verifyFilterStatus]);

	const verifyDistrictOptions = useMemo(() => {
		const map = new Map<number, string>();
		const districtsData = Array.isArray(secretaryVerifyReport?.districts) ? secretaryVerifyReport.districts : [];
		districtsData.forEach((district: any) => {
			const districtId = Number(district?.district_id || 0);
			const districtName = String(district?.district_name || '');
			if (districtId > 0 && districtName) {
				map.set(districtId, districtName);
			}
		});
		return Array.from(map.entries())
			.map(([id, name]) => ({ id, name }))
			.sort((a, b) => a.name.localeCompare(b.name, 'pl'));
	}, [secretaryVerifyReport]);

	const handleRunCopy = () => {
		if (selectedCopyItems.length === 0) {
			onMessage({ msg: 'Brak zaznaczonych pozycji do kopiowania.', severity: 'warning' });
			return;
		}
		const accepted = window.confirm(`Uruchomić kopiowanie ${selectedCopyItems.length} plików?`);
		if (!accepted) {
			return;
		}
		setIsSaving(true);
		runStage3DriveCopy(token, {
			items: selectedCopyItems.map((item) => ({
				student_id: item.student_id,
				work_code: item.work_code,
				source_file_id: item.source_file_id,
				source_file_name: item.source_file_name,
				target_folder_id: item.target_folder_id,
				district_id: item.district_id,
				jury_pair_id: item.jury_pair_id,
			})),
		})
			.then((response) => {
				if (response.status === 200) {
					setCopyRunReport(response.data);
					const copied = Number(response.data?.summary?.copied_total || 0);
					const failed = Number(response.data?.summary?.failed_total || 0);
					onMessage({ msg: `Kopiowanie zakończone. Sukces: ${copied}, błędy: ${failed}.`, severity: 'success' });
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się uruchomić kopiowania.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd wykonywania kopiowania.' });
			})
			.finally(() => setIsSaving(false));
	};

	const getCandidateStatus = (item: CopyCandidate): string => {
		if (item.drive_copied) {
			return 'Skopiowano wcześniej';
		}
		if (item.source_error) {
			return `Błąd źródła: ${item.source_error}`;
		}
		if (!item.target_folder_id) {
			return 'Brak folderu komisji';
		}
		if (item.source_match_count <= 0) {
			return 'Brak pliku źródłowego';
		}
		if (item.source_match_count > 1) {
			return 'Duplikaty nazwy kodu (różne pliki)';
		}
		return 'Gotowe do kopiowania';
	};

	const openInlinePreview = (row: FoundScanRow) => {
		if (!row.source_file_id && !row.drive_view_url) {
			onMessage({ msg: 'Brak pliku do podglądu.', severity: 'warning' });
			return;
		}
		setPreviewRowKey(`${row.district_id}-${row.work_code}-${row.source_file_id}`);
		setPreviewOpen(true);
	};

	const foundRowKey = (row: FoundScanRow): string => `${row.district_id}-${row.work_code}-${row.source_file_id}`;
	const previewIndex = useMemo(() => {
		if (!previewRowKey) {
			return -1;
		}
		return foundScanRows.findIndex((row) => foundRowKey(row) === previewRowKey);
	}, [foundScanRows, previewRowKey]);
	const previewCurrentRow = previewIndex >= 0 ? foundScanRows[previewIndex] : null;
	const previewHasPrev = previewIndex > 0;
	const previewHasNext = previewIndex >= 0 && previewIndex < foundScanRows.length - 1;

	const goToPreviousPreview = () => {
		if (!previewHasPrev) {
			return;
		}
		const prev = foundScanRows[previewIndex - 1];
		if (prev) {
			setPreviewRowKey(foundRowKey(prev));
		}
	};

	const goToNextPreview = () => {
		if (!previewHasNext) {
			return;
		}
		const next = foundScanRows[previewIndex + 1];
		if (next) {
			setPreviewRowKey(foundRowKey(next));
		}
	};

	const updateFoundDetailLocalStatus = (row: FoundScanRow, status: string) => {
		setSecretaryVerifyReport((prev: any) => {
			if (!prev || !Array.isArray(prev.districts)) {
				return prev;
			}
			const nextDistricts = prev.districts.map((district: any) => {
				const details = Array.isArray(district?.found_details) ? district.found_details : [];
				const nextDetails = details.map((detail: any) => {
					const sameStudent = Number(detail?.student_id || 0) === row.student_id;
					const sameFile = String(detail?.source_file_id || '') === row.source_file_id;
					if (!sameStudent && !sameFile) {
						return detail;
					}
					return {
						...detail,
						existing_status: status,
						was_already_noted: true,
					};
				});
				return {
					...district,
					found_details: nextDetails,
				};
			});
			return {
				...prev,
				districts: nextDistricts,
			};
		});
	};

	const handlePreviewAutoScanOkAndNext = () => {
		const row = previewCurrentRow;
		if (!row || !row.student_id) {
			onMessage({ msg: 'Brak student_id dla wybranego skanu.', severity: 'warning' });
			return;
		}
		const nextRow = previewHasNext ? foundScanRows[previewIndex + 1] : null;
		setIsSaving(true);
		markStage3AutoScanOkStudent(token, row.student_id)
			.then((response) => {
				if (response.status !== 200) {
					onMessage({ msg: response.data?.message || 'Nie udało się ustawić AUTOSKANOK.' });
					return;
				}
				onMessage({ msg: `Ustawiono AUTOSKANOK dla ucznia #${row.student_id}.`, severity: 'success' });
				updateFoundDetailLocalStatus(row, 'AUTOSKANOK');
				if (nextRow) {
					setPreviewRowKey(foundRowKey(nextRow));
				} else {
					setPreviewOpen(false);
					setPreviewRowKey(null);
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd ustawiania AUTOSKANOK.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleMarkAutoScanOkSingle = (row: FoundScanRow) => {
		if (!row.student_id) {
			onMessage({ msg: 'Brak student_id dla wybranego skanu.', severity: 'warning' });
			return;
		}
		setIsSaving(true);
		markStage3AutoScanOkStudent(token, row.student_id)
			.then((response) => {
				if (response.status === 200) {
					onMessage({ msg: `Ustawiono AUTOSKANOK dla ucznia #${row.student_id}.`, severity: 'success' });
					handleVerifySecretaries();
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się ustawić AUTOSKANOK.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd ustawiania AUTOSKANOK.' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleMarkAutoScanOkDistrict = (districtId: number, districtName: string) => {
		if (districtId <= 0) {
			onMessage({ msg: 'Nieprawidłowy okręg.', severity: 'warning' });
			return;
		}
		const accepted = window.confirm(`Ustawić AUTOSKANOK dla całego okręgu "${districtName}" (status AUTO-SKAN)?`);
		if (!accepted) {
			return;
		}
		setIsSaving(true);
		markStage3AutoScanOkDistrict(token, districtId)
			.then((response) => {
				if (response.status === 200) {
					const updated = Number(response.data?.updated_count || 0);
					onMessage({ msg: `Ustawiono AUTOSKANOK w okręgu "${districtName}": ${updated}.`, severity: 'success' });
					handleVerifySecretaries();
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się ustawić AUTOSKANOK dla okręgu.' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd ustawiania AUTOSKANOK dla okręgu.' });
			})
			.finally(() => setIsSaving(false));
	};

	const parseRawRotationInput = (raw: string): Record<number, number> => {
		const result: Record<number, number> = {};
		const tokens = String(raw || '')
			.split(',')
			.map((x) => String(x).trim())
			.filter(Boolean);
		tokens.forEach((token) => {
			const [pagePart, degPart] = token.split(':').map((x) => String(x).trim());
			const page = Number(pagePart);
			if (!Number.isFinite(page) || page <= 0) {
				return;
			}
			const deg = degPart === undefined || degPart === '' ? 180 : Number(degPart);
			if (!Number.isFinite(deg)) {
				return;
			}
			const normalized = ((Math.trunc(deg) % 360) + 360) % 360;
			if (normalized !== 90 && normalized !== 180 && normalized !== 270) {
				return;
			}
			result[Math.trunc(page)] = normalized;
		});
		return result;
	};

	const parsePageListInput = (raw: string): number[] => {
		const values = new Set<number>();
		const tokens = String(raw || '')
			.split(',')
			.map((x) => String(x).trim())
			.filter(Boolean);
		tokens.forEach((token) => {
			const rangeMatch = token.match(/^(\d+)\s*-\s*(\d+)$/);
			if (rangeMatch) {
				const from = Number(rangeMatch[1]);
				const to = Number(rangeMatch[2]);
				if (Number.isFinite(from) && Number.isFinite(to) && from > 0 && to > 0) {
					const start = Math.min(from, to);
					const end = Math.max(from, to);
					for (let p = start; p <= end; p++) values.add(p);
				}
				return;
			}
			const page = Number(token);
			if (Number.isFinite(page) && page > 0) {
				values.add(Math.trunc(page));
			}
		});
		return Array.from(values).sort((a, b) => a - b);
	};

	const handleToolsFolderPresetChange = (value: string) => {
		setToolsFolderPreset(value);
		const selected = toolsFolderPresetOptions.find((opt) => opt.value === value);
		if (selected && selected.folderId !== '') {
			setToolsBaseFolderId(selected.folderId);
		}
	};

	const handleToolsSearchFiles = () => {
		const baseFolderId = String(toolsBaseFolderId || '').trim();
		if (baseFolderId === '') {
			onMessage({ msg: 'Podaj ID katalogu bazowego.', severity: 'warning' });
			return;
		}
		setIsSaving(true);
		searchStage3DriveFolderFiles(token, {
			base_folder_id: baseFolderId,
			query: String(toolsSearchQuery || '').trim(),
			limit: 300,
		})
			.then((response) => {
				if (response.status === 200) {
					const items = Array.isArray(response.data?.items) ? response.data.items : [];
					setToolsFiles(items as Stage3RawRotateFileRow[]);
					setToolsTotalMatched(Number(response.data?.total_matched || items.length));
					onMessage({
						msg: `Znaleziono plików: ${Number(response.data?.total_matched || items.length)}.`,
						severity: 'success',
					});
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się wyszukać plików.', severity: 'error' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd wyszukiwania plików.', severity: 'error' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleToolsRotateFile = (item: Stage3RawRotateFileRow) => {
		const input = window.prompt('Podaj mapę rotacji stron (np. 2:90,3:270,4:180; samo "2,4" = 180°).', '');
		if (input === null) {
			return;
		}
		const pageRotations = parseRawRotationInput(input);
		if (Object.keys(pageRotations).length === 0) {
			onMessage({ msg: 'Brak poprawnej mapy rotacji (90/180/270).', severity: 'warning' });
			return;
		}
		const outputNameRaw = window.prompt(
			`Nazwa pliku wynikowego (opcjonalnie, .pdf zostanie dopisane):`,
			String(item.drive_file_name || ''),
		);
		if (outputNameRaw === null) {
			return;
		}
		const targetFolderId = String(toolsTargetFolderId || '').trim();

		setIsSaving(true);
		rotateStage3RawDriveFile(token, {
			drive_file_id: String(item.drive_file_id),
			page_rotations: pageRotations,
			target_folder_id: targetFolderId,
			output_name: String(outputNameRaw || '').trim(),
		})
			.then((response) => {
				if (response.status === 200) {
					const fileName = String(response.data?.created_drive_file_name || 'nowy plik');
					onMessage({ msg: `Utworzono obróconą kopię: ${fileName}.`, severity: 'success' });
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się utworzyć kopii obrotu.', severity: 'error' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd tworzenia kopii obrotu.', severity: 'error' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleToolsAddMergePages = (item: Stage3RawRotateFileRow) => {
		const input = window.prompt('Podaj strony do scalenia z tego pliku (np. 1,2,5-7).', '');
		if (input === null) return;
		const pages = parsePageListInput(input);
		if (pages.length === 0) {
			onMessage({ msg: 'Brak poprawnych stron do dodania.', severity: 'warning' });
			return;
		}
		setToolsMergeQueue((prev) => [
			...prev,
			...pages.map((page) => ({
				key: `${item.drive_file_id}:${page}:${Date.now()}:${Math.random()}`,
				drive_file_id: String(item.drive_file_id),
				drive_file_name: String(item.drive_file_name || ''),
				page,
			})),
		]);
	};

	const handleToolsRunMerge = () => {
		if (toolsMergeQueue.length === 0) {
			onMessage({ msg: 'Kolejka scalania jest pusta.', severity: 'warning' });
			return;
		}
		const outputNameRaw = window.prompt('Nazwa pliku wynikowego (opcjonalnie):', 'merged_pages.pdf');
		if (outputNameRaw === null) return;
		setIsSaving(true);
		mergeStage3DrivePages(token, {
			items: toolsMergeQueue.map((row) => ({ drive_file_id: row.drive_file_id, page: row.page })),
			target_folder_id: String(toolsTargetFolderId || '').trim(),
			output_name: String(outputNameRaw || '').trim(),
		})
			.then((response) => {
				if (response.status === 200) {
					onMessage({
						msg: `Scalono strony. Utworzono: ${String(response.data?.created_drive_file_name || '-')}.`,
						severity: 'success',
					});
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się scalić stron.', severity: 'error' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd scalania stron.', severity: 'error' });
			})
			.finally(() => setIsSaving(false));
	};

	const handleToolsSetReplaceBase = (item: Stage3RawRotateFileRow) => {
		setToolsReplaceBase({
			drive_file_id: String(item.drive_file_id),
			drive_file_name: String(item.drive_file_name || ''),
		});
	};

	const handleToolsAddReplaceMapping = (item: Stage3RawRotateFileRow) => {
		if (!toolsReplaceBase?.drive_file_id) {
			onMessage({ msg: 'Najpierw ustaw dokument bazowy (Baza).', severity: 'warning' });
			return;
		}
		const input = window.prompt(
			'Podaj mapowanie target=source, np. 2=1,5=3 (strona bazowa = strona z tego pliku).',
			'',
		);
		if (input === null) return;
		const tokens = String(input || '')
			.split(',')
			.map((x) => String(x).trim())
			.filter(Boolean);
		const nextRows: Array<{
			key: string;
			target_page: number;
			source_drive_file_id: string;
			source_drive_file_name: string;
			source_page: number;
		}> = [];
		tokens.forEach((token) => {
			const pair = token.split('=').map((x) => String(x).trim());
			if (pair.length !== 2) return;
			const target = Number(pair[0]);
			const source = Number(pair[1]);
			if (!Number.isFinite(target) || !Number.isFinite(source) || target <= 0 || source <= 0) return;
			nextRows.push({
				key: `${Math.trunc(target)}:${item.drive_file_id}:${Math.trunc(source)}:${Date.now()}:${Math.random()}`,
				target_page: Math.trunc(target),
				source_drive_file_id: String(item.drive_file_id),
				source_drive_file_name: String(item.drive_file_name || ''),
				source_page: Math.trunc(source),
			});
		});
		if (nextRows.length === 0) {
			onMessage({ msg: 'Brak poprawnych mapowań target=source.', severity: 'warning' });
			return;
		}
		setToolsReplaceMappings((prev) => {
			const byTarget = new Map<number, any>();
			prev.forEach((row) => byTarget.set(Number(row.target_page), row));
			nextRows.forEach((row) => byTarget.set(Number(row.target_page), row));
			return Array.from(byTarget.values()).sort((a, b) => Number(a.target_page) - Number(b.target_page));
		});
	};

	const handleToolsRunReplace = () => {
		if (!toolsReplaceBase?.drive_file_id) {
			onMessage({ msg: 'Brak dokumentu bazowego do podmiany.', severity: 'warning' });
			return;
		}
		if (toolsReplaceMappings.length === 0) {
			onMessage({ msg: 'Brak mapowań podmiany stron.', severity: 'warning' });
			return;
		}
		const outputNameRaw = window.prompt('Nazwa pliku wynikowego po podmianie (opcjonalnie):', '');
		if (outputNameRaw === null) return;
		setIsSaving(true);
		replaceStage3DrivePages(token, {
			base_drive_file_id: String(toolsReplaceBase.drive_file_id),
			replacements: toolsReplaceMappings.map((row) => ({
				target_page: Number(row.target_page),
				source_drive_file_id: String(row.source_drive_file_id),
				source_page: Number(row.source_page),
			})),
			target_folder_id: String(toolsTargetFolderId || '').trim(),
			output_name: String(outputNameRaw || '').trim(),
		})
			.then((response) => {
				if (response.status === 200) {
					onMessage({
						msg: `Utworzono PDF po podmianie stron: ${String(response.data?.created_drive_file_name || '-')}.`,
						severity: 'success',
					});
				} else {
					onMessage({ msg: response.data?.message || 'Nie udało się podmienić stron.', severity: 'error' });
				}
			})
			.catch((exception) => {
				onMessage({ msg: exception?.response?.data?.message || 'Błąd podmiany stron.', severity: 'error' });
			})
			.finally(() => setIsSaving(false));
	};

	return (
		<Dialog
			open={open}
			onClose={busy ? undefined : onClose}
			fullWidth
			maxWidth='xl'
		>
			<DialogTitle>Zarządzanie Etapem III</DialogTitle>
			<DialogContent>
				<Tabs
					value={activeTab}
					onChange={(_event, next) => setActiveTab(next)}
					variant='scrollable'
					scrollButtons='auto'
					sx={{ mb: 2 }}
				>
					<Tab
						value='secretaries'
						label='Sekretarze'
					/>
					<Tab
						value='pairs'
						label='Komisje'
					/>
					<Tab
						value='assign'
						label='Przydzielanie'
					/>
					<Tab
						value='copy'
						label='Kopiowanie'
					/>
					<Tab
						value='verify'
						label='Weryfikacja'
					/>
					<Tab
						value='setup'
						label='Drive setup'
					/>
					<Tab
						value='tools'
						label='Narzędzia plików'
					/>
				</Tabs>

				{activeTab === 'secretaries' && (
					<Stage3DriveSecretariesTab
						busy={busy}
						secretarySettings={secretarySettings}
						onSecretaryFieldChange={handleSecretaryFieldChange}
						secretariesParentFolderUrl={secretariesParentFolderUrl}
						setSecretariesParentFolderUrl={setSecretariesParentFolderUrl}
						shareMessage={shareMessage}
						setShareMessage={setShareMessage}
						testRecipientEmail={testRecipientEmail}
						setTestRecipientEmail={setTestRecipientEmail}
						sendNotificationEmail={sendNotificationEmail}
						setSendNotificationEmail={setSendNotificationEmail}
						shareToSystemEmail={shareToSystemEmail}
						setShareToSystemEmail={setShareToSystemEmail}
						shareToSecondaryEmail={shareToSecondaryEmail}
						setShareToSecondaryEmail={setShareToSecondaryEmail}
						shareEnabled={Boolean(testRecipientEmail.trim()) || shareToSystemEmail || shareToSecondaryEmail}
						overwriteExisting={overwriteExisting}
						setOverwriteExisting={setOverwriteExisting}
						onCreateSecretaryFolders={handleCreateSecretaryFolders}
						onVerifySecretaryFolders={handleVerifySecretaryFoldersOnly}
						onShareSecretaryFolders={handleShareSecretaryFoldersOnly}
						onSaveSettings={handleSaveDriveSetup}
						secretariesActionReport={secretariesActionReport}
						secretaryVerifyStatusByDistrict={secretaryVerifyStatusByDistrict}
						secretaryVerifyErrorByDistrict={secretaryVerifyErrorByDistrict}
					/>
				)}

				{activeTab === 'pairs' && (
					<Stage3JuryPairsManagementTab
						districts={districts}
						draft={draft}
						onDraftChange={setDraft}
						onDraftReset={() => setDraft(EMPTY_DRAFT)}
						onSavePair={handleSavePair}
						pairs={pairs}
						onEditPair={(pair: Stage3JuryPairItem) =>
							setDraft({
								id: pair.id,
								name: pair.name,
								juror1_name: pair.juror1_name,
								juror1_email: pair.juror1_email || '',
								juror2_name: pair.juror2_name,
								juror2_email: pair.juror2_email || '',
								juror1_district_id: pair.juror1_district_id ?? '',
								juror2_district_id: pair.juror2_district_id ?? '',
								drive_folder_url: pair.drive_folder_url,
								notes: pair.notes || '',
							})
						}
						onDeletePair={handleDeletePair}
						juryParentFolderUrl={juryParentFolderUrl}
						setJuryParentFolderUrl={setJuryParentFolderUrl}
						shareMessage={shareMessage}
						setShareMessage={setShareMessage}
						testRecipientEmail={testRecipientEmail}
						setTestRecipientEmail={setTestRecipientEmail}
						sendNotificationEmail={sendNotificationEmail}
						overwriteExisting={overwriteExisting}
						onCreateJuryFolders={handleCreateJuryFolders}
						onVerifyJuryFolders={handleVerifyJuryFoldersOnly}
						onShareJuryFolders={handleShareJuryFoldersOnly}
						juryActionReport={juryActionReport}
						juryVerifyStatusByPairId={juryVerifyStatusByPairId}
						juryVerifyErrorByPairId={juryVerifyErrorByPairId}
						busy={busy}
					/>
				)}

				{activeTab === 'assign' && (
					<Stage3AssignmentTab
						autoAssignMode={autoAssignMode}
						onAutoAssignModeChange={setAutoAssignMode}
						onAutoAssign={handleAutoAssign}
						busy={busy}
					/>
				)}

				{activeTab === 'setup' && (
					<Stage3DriveSetupTab
						driveAccessToken={driveAccessToken}
						driveRefreshToken={driveRefreshToken}
						driveClientId={driveClientId}
						driveClientSecret={driveClientSecret}
						setDriveAccessToken={setDriveAccessToken}
						setDriveRefreshToken={setDriveRefreshToken}
						setDriveClientId={setDriveClientId}
						setDriveClientSecret={setDriveClientSecret}
						driveSetupInfo={driveSetupInfo}
						onSaveDriveSetup={handleSaveDriveSetup}
						onDriveHealthCheck={handleDriveHealthCheck}
						healthReport={healthReport}
						busy={busy}
						secretariesParentFolderUrl={secretariesParentFolderUrl}
						juryParentFolderUrl={juryParentFolderUrl}
					/>
				)}

				{activeTab === 'copy' && (
					<Stage3DriveCopyTab
						busy={busy}
						copyCandidates={copyCandidates}
						selectedCopyItemsCount={selectedCopyItems.length}
						selectedCopyKeys={selectedCopyKeys}
						candidateKey={candidateKey}
						copySortMode={copySortMode}
						setCopySortMode={setCopySortMode}
						onLoadCopyCandidates={handleLoadCopyCandidates}
						onSelectAllCopyAllowed={selectAllCopyAllowed}
						onClearCopySelection={clearCopySelection}
						onRunCopy={handleRunCopy}
						onToggleCopySelection={toggleCopySelection}
						getCandidateStatus={getCandidateStatus}
						sortedCopyCandidates={sortedCopyCandidates}
						copyRunReport={copyRunReport}
					/>
				)}

				{activeTab === 'verify' && (
					<Stage3DriveVerificationTab
						busy={busy}
						onVerifySecretaries={handleVerifySecretaries}
						onVerifyJuryFolders={handleVerifyJuryFolders}
						secretaryVerifyReport={secretaryVerifyReport}
						juryVerifyReport={juryVerifyReport}
						verifySortMode={verifySortMode}
						setVerifySortMode={setVerifySortMode}
						verifyFilterDistrictId={verifyFilterDistrictId}
						setVerifyFilterDistrictId={setVerifyFilterDistrictId}
						verifyFilterStatus={verifyFilterStatus}
						setVerifyFilterStatus={setVerifyFilterStatus}
						verifyDistrictOptions={verifyDistrictOptions}
						foundScanRows={foundScanRows}
						onOpenInlinePreview={openInlinePreview}
						onMarkAutoScanOkSingle={handleMarkAutoScanOkSingle}
						onMarkAutoScanOkDistrict={handleMarkAutoScanOkDistrict}
					/>
				)}

				{activeTab === 'tools' && (
					<Stage3RawRotateTab
						busy={busy}
						folderPreset={toolsFolderPreset}
						setFolderPreset={handleToolsFolderPresetChange}
						folderPresetOptions={toolsFolderPresetOptions}
						baseFolderId={toolsBaseFolderId}
						setBaseFolderId={setToolsBaseFolderId}
						searchQuery={toolsSearchQuery}
						setSearchQuery={setToolsSearchQuery}
						targetFolderId={toolsTargetFolderId}
						setTargetFolderId={setToolsTargetFolderId}
						toolFiles={toolsFiles}
						totalMatched={toolsTotalMatched}
						onSearch={handleToolsSearchFiles}
						onRotate={handleToolsRotateFile}
						mergeQueue={toolsMergeQueue}
						onAddMergePages={handleToolsAddMergePages}
						onRemoveMergeItem={(key) => setToolsMergeQueue((prev) => prev.filter((row) => row.key !== key))}
						onClearMergeQueue={() => setToolsMergeQueue([])}
						onRunMerge={handleToolsRunMerge}
						replaceBaseFileId={String(toolsReplaceBase?.drive_file_id || '')}
						replaceBaseFileName={String(toolsReplaceBase?.drive_file_name || '')}
						onSetReplaceBase={handleToolsSetReplaceBase}
						replaceMappings={toolsReplaceMappings}
						onAddReplaceMapping={handleToolsAddReplaceMapping}
						onRemoveReplaceMapping={(key) => setToolsReplaceMappings((prev) => prev.filter((row) => row.key !== key))}
						onClearReplaceMappings={() => setToolsReplaceMappings([])}
						onRunReplace={handleToolsRunReplace}
					/>
				)}

				<Stage3DrivePreviewDialog
					open={previewOpen}
					onClose={() => {
						setPreviewOpen(false);
						setPreviewRowKey(null);
					}}
					busy={busy}
					previewIndex={previewIndex}
					totalCount={foundScanRows.length}
					previewCurrentRow={previewCurrentRow}
					previewHasPrev={previewHasPrev}
					previewHasNext={previewHasNext}
					onPrev={goToPreviousPreview}
					onNext={goToNextPreview}
					onAutoScanOkAndNext={handlePreviewAutoScanOkAndNext}
				/>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onClose}
					disabled={busy}
				>
					Zamknij
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default Stage3JuryPairsDialog;
